import { Avatar as DiceBearAvatar, type Style } from '@dicebear/core'
import { UserRound } from 'lucide-react'
import { useMemo } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { useAvatarStyle } from '@/components/profile/avatar-style'
import { configForCatalog } from '@/lib/avatar-catalog'

const sizes = { sm: 'size-6', md: 'size-8', lg: 'size-16' } as const
const icons = { sm: 'size-3.5', md: 'size-4', lg: 'size-7' } as const
export type AvatarSize = keyof typeof sizes

function renderUri(style: Style, options: Record<string, unknown>, seed: string) {
  return new DiceBearAvatar(style, { size: 128, seed, ...options }).toDataUri()
}

/**
 * Renders an account's DiceBear avatar client-side from its native options.
 *
 * Replaces `AccountAvatar` (backend-rendered `avatar_url`): since backend feature 054/B1
 * rows carry the options object verbatim instead of a pre-rendered image address, drawing
 * happens here with `@dicebear/core` against the shared `useAvatarStyle()` artwork. Options
 * may be partial (a user who only edited some controls). They are first canonicalised
 * against the matching backend catalogue; DiceBear fills the rest from the uploaded
 * style's seed/weights.
 */
export function AvatarPortrait({
  options,
  seed,
  alt = '',
  size = 'md',
  className,
}: {
  options?: Record<string, unknown> | null
  seed: string
  alt?: string
  size?: AvatarSize
  className?: string
}) {
  const { style, catalog } = useAvatarStyle()
  const renderOptions = useMemo(
    () =>
      catalog && options ? configForCatalog(options, catalog.controls, catalog.default) : undefined,
    [catalog, options],
  )
  const uri = useMemo(
    () => (style && renderOptions ? renderUri(style, renderOptions, seed) : undefined),
    [renderOptions, seed, style],
  )
  return (
    <Avatar className={cn(sizes[size], className)}>
      {uri && <AvatarImage src={uri} alt={alt} />}
      <AvatarFallback delayMs={600}>
        <UserRound className={cn('text-muted-foreground', icons[size])} aria-hidden />
      </AvatarFallback>
    </Avatar>
  )
}
